import {TFunction} from 'react-i18next';
import Excel from 'exceljs';
import {
	renderHeaderXL,
	createZIPXL,
	renderPhaseDateXL,
	renderDateXL,
	renderArrayXL,
	renderHTMLStringXL,
	XLColumn,
	mergeColumnsExcel,
	getColumnAlphabetExcel,
} from './ExcelExportCommon';
import {RegulatoryDocument} from 'types';
import {InfinityListColumn} from '../InfinityList';

interface IExcel {
	(
		entityListiRegDocs: any[],
		column: InfinityListColumn[],
		t: TFunction,
	): Promise<void>;
}

export const getColumnsXLRender = (t: TFunction) => [
	{
		key: 'tooltip',
		xlName: ' ',
		xlRender: (iRegDoc: RegulatoryDocument) =>
			renderHTMLStringXL('', iRegDoc.summary),
		xlWidth: 80,
	},
	{
		key: 'regulationNumber',
	},
	{
		key: 'version',
		xlRender: (iRegDoc: RegulatoryDocument) => ({
			text: `${iRegDoc.regulation?.regulationNumber ?? ''} | ${iRegDoc.name}`,
			hyperlink: `${window.location.origin}/regulatoryDocuments/${iRegDoc.id}`,
		}),

		xlFontStyle: {
			underline: true,
			bold: false,
			color: {argb: '00B0F0'},
		},
		xlWidth: 40,
	},
	{
		key: 'modifiedAt',
		xlRender: (iRegDoc: RegulatoryDocument) =>
			renderDateXL(iRegDoc.workflow?.clearingTimestamp),
	},
	{
		key: 'contactPersons',
		xlRender: (iRegDoc: RegulatoryDocument) =>
			renderArrayXL(iRegDoc.contactPersons),
	},
	{
		key: 'markets',
		xlRender: (iRegDoc: RegulatoryDocument) => renderArrayXL(iRegDoc.markets),
		xlWidth: 40,
	},
	{
		key: 'mainKeywords',
		xlRender: (iRegDoc: RegulatoryDocument) =>
			renderArrayXL(iRegDoc.mainKeywords),
		xlWidth: 40,
	},
	{
		key: 'keywords',
		xlRender: (iRegDoc: RegulatoryDocument) => renderArrayXL(iRegDoc.keywords),
		xlWidth: 40,
	},
	{
		key: 'status',
		xlRender: (iRegDoc: RegulatoryDocument) =>
			t(iRegDoc.status, {
				ns: 'common/enums',
				keyPrefix: 'RegulatoryDocumentStatus',
			}),
	},
	{
		key: 'dateEffective',
		xlRender: (iRegDoc: RegulatoryDocument) =>
			renderDateXL(iRegDoc.dateEffective),
	},
	{
		key: 'dateNewTypes',
		xlRender: (iRegDoc: RegulatoryDocument) =>
			renderDateXL(iRegDoc.dateNewTypes),
	},
	{
		key: 'dateNewRegistration',
		xlRender: (iRegDoc: RegulatoryDocument) =>
			renderDateXL(iRegDoc.dateNewRegistration),
	},
	{
		key: 'comprehensive',
		xlRender: (iRegDoc: RegulatoryDocument) =>
			renderDateXL(iRegDoc.comprehensive),
	},
	{
		key: 'numberOfSegments',
	},
	{
		key: 'disassemblyStatus',
		xlRender: (iRegDoc: RegulatoryDocument) =>
			t(iRegDoc.disassemblyStatus, {
				ns: 'common/enums',
				keyPrefix: 'RegulatoryDocumentDisassemblyStatus',
			}),
	},
	{
		key: 'driveVariants',
		xlRender: (iRegDoc: RegulatoryDocument) =>
			renderArrayXL(iRegDoc.driveVariants),
	},
	{
		key: 'vehicleCategories',
		xlRender: (iRegDoc: RegulatoryDocument) =>
			renderArrayXL(iRegDoc.vehicleCategories),
	},
	{
		key: 'vexClusters',
		xlRender: (iRegDoc: RegulatoryDocument) =>
			renderArrayXL(iRegDoc.vexClusters),
	},
	{
		key: 'modelYear',
		xlRender: (iRegDoc: RegulatoryDocument) => iRegDoc.modelYear ?? '',
	},
	{
		key: 'phaseIn',
		xlRender: (iRegDoc: RegulatoryDocument) =>
			renderPhaseDateXL(iRegDoc.phaseIn),
	},
	{
		key: 'phaseOut',
		xlRender: (iRegDoc: RegulatoryDocument) =>
			renderPhaseDateXL(iRegDoc.phaseOut),
	},
	{
		key: 'workflowStatus',
		xlRender: (iRegDoc: RegulatoryDocument) =>
			t(iRegDoc.workflow.status, {
				ns: 'common/enums',
				keyPrefix: 'WorkflowStatus',
			}),
	},
];

export const handleClickVoProPage: IExcel = async (
	entityListiRegDocs,
	columns,
	t,
) => {
	const arrRegDocFilt = entityListiRegDocs;

	const strPageName = t('RegulatoryDocuments', {
		ns: 'appShell/sidenavigation',
		keyPrefix: 'SideNavigation',
	});

	const columnsXLRender = getColumnsXLRender(t);

	const columnsXL: XLColumn[] = mergeColumnsExcel(columns, columnsXLRender);

	const arrColWidth = columnsXL.map(x => x.xlWidth ?? 25);

	const workbook = new Excel.Workbook();
	const sheet = workbook.addWorksheet('TRACE_' + strPageName);
	renderHeaderXL(workbook, sheet, arrColWidth, strPageName, t);

	let intRowCount = 4;
	let row = sheet.getRow(intRowCount);
	row.font = {
		bold: true,
		name: 'Porsche Next TT',
		size: 10,
		color: {argb: 'FFFFFFFF'},
	};
	sheet.addTable({
		name: 'tableVoPro',
		ref: 'B' + String(intRowCount),
		style: {
			showRowStripes: true,
		},
		columns: columnsXL,
		rows: [],
	});

	intRowCount++;
	const table = sheet.getTable('tableVoPro');

	arrRegDocFilt.forEach((iRegDoc: RegulatoryDocument) => {
		row = sheet.getRow(intRowCount);
		row.alignment = {wrapText: true, vertical: 'top'};

		columnsXL.forEach((col, index) => {
			const colAlpha = getColumnAlphabetExcel(index + 1);
			if (col?.xlFontStyle) {
				row.getCell(colAlpha).style.font = col.xlFontStyle;
			}
		});

		const renderedXLRow = columnsXL.map(x => {
			const xlRenderFnDef = () => '';
			const xlRenderFn = x.xlRender ?? xlRenderFnDef;
			return xlRenderFn(iRegDoc);
		});

		table.addRow(renderedXLRow);

		intRowCount++;
	});

	table.commit();
	const strZipName = strPageName;
	await createZIPXL(workbook, strZipName, t);
};
